import type { OrderStatus } from "@pufimi/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { OrdersPagination } from "@/components/orders/orders-pagination";
import { OrdersTable } from "@/components/orders/orders-table";
import { adminOrdersQueryKey } from "@/lib/admin-queries";
import { adminApi } from "@/lib/api";

function OrdersRoute() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: adminOrdersQueryKey(status ?? undefined, page),
    queryFn: () => adminApi.orders.list({ status: status ?? undefined, page }),
    // Keep the previous rows on screen while switching filters/pages — the chips
    // and pager stay put and the table doesn't flash empty.
    placeholderData: keepPreviousData,
  });

  // Changing the filter resets to page 1 — page 3 of "all" is meaningless once
  // the result set narrows to a status.
  const handleStatusChange = (next: OrderStatus | null) => {
    setStatus(next);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Zamówienia</h1>
        <p className="text-muted-foreground">Wszystkie zamówienia, od najnowszych.</p>
      </header>

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">Nie udało się wczytać zamówień.</p>
      ) : (
        <div className="space-y-4">
          <OrdersTable
            orders={query.data.orders}
            activeStatus={status}
            onStatusChange={handleStatusChange}
            renderOrderLink={(orderId, children, className) => (
              <Link to="/orders/$orderId" params={{ orderId }} className={className}>
                {children}
              </Link>
            )}
            onOpenOrder={(orderId) => navigate({ to: "/orders/$orderId", params: { orderId } })}
          />
          <OrdersPagination
            page={query.data.page}
            pageSize={query.data.pageSize}
            total={query.data.total}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/orders/")({ component: OrdersRoute });
